import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { clsx } from "clsx";
import { ButtonProps } from "../ui/button";
import ImageSvg from "../ui/image-svg";

type ImageProps = {
  src: string;
  alt?: string;
};

type VideoProps = {
  image: ImageProps;
  url: string;
};

type TabProps = {
  heading: string;
  description: string;
  image?: ImageProps;
  video?: VideoProps;
};

type Props = {
  tagline: string;
  heading: string;
  description: string;
  buttons: ButtonProps[];
  tabs: TabProps[];
};

export type ValoresSectionProps = React.ComponentPropsWithoutRef<"section"> &
  Partial<Props>;

export const ValoresSection = (props: ValoresSectionProps) => {
  const { tagline, heading, description, tabs } = {
    ...ValoresSectionDefaults,
    ...props,
  };
  const [activeTab, setActiveTab] = useState(0);
  return (
    <section className=" py-10 md:py-24 lg:py-28 ">
      <div className="container">
        <div className="mx-auto mb-12  text-center md:mb-18 lg:mb-20 max-w-3xl">
          <p className="mb-3 text-n-text-light md:mb-4">{tagline}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-n-primary mb-6 ">
            {heading}
          </h2>
          <p className="text-n-text-neutral md:text-lg mb-8">{description}</p>
        </div>

        <div className="grid grid-cols-1 items-center gap-x-12 md:grid-cols-2 lg:gap-x-20">
          <div className="relative mb-8 grid auto-cols-fr grid-cols-1 grid-rows-[auto_auto] items-start md:mb-0 md:items-stretch">
            {tabs.map((tab, index) => (
              <div
                key={index}
                onClick={() => setActiveTab(index)}
                className={clsx(
                  "cursor-pointer border-b border-border-primary py-6",
                  {
                    "opacity-100": activeTab === index,
                    "opacity-25": activeTab !== index,
                  },
                )}
              >
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-semibold text-n-text-green md:text-3xl md:leading-[1.3] lg:text-4xl">
                    {tab.heading}
                  </h2>
                </div>
                <motion.div
                  initial={false}
                  animate={{
                    height: activeTab === index ? "auto" : 0,
                    opacity: activeTab === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <p className="mt-3 text-n-text-neutral md:mt-4">
                    {tab.description}
                  </p>
                </motion.div>
              </div>
            ))}
          </div>
          <div className="max-h-full w-full justify-center overflow-hidden">
            <AnimatePresence mode="wait" initial={false}>
              {tabs.map((tab, index) => {
                if (activeTab !== index) return null;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    exit={{ opacity: 0 }}
                  >
                    {tab.image && (
                      <ImageSvg
                        src={tab.image.src}
                        alt={tab.image.alt}
                        className="h-full w-[80%] mx-auto"
                      />
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export const ValoresSectionDefaults: Props = {
  tagline: "",
  heading: "Trabalhamos em cima de valores e pilares sólidos.",
  description:
    "Para garantir uma experiência superior, com mais facilidade e agilidade, temos em nosso DNA estes valores que solidificam a nossa busca por excelência.",
  buttons: [],
  tabs: [
    {
      heading: "Satisfação de Nossos Clientes",
      description:
        "A base de todas as nossas atividades é a satisfação de nossos clientes. Em um mercado tradicional e engessado, buscamos alternativas inovadores para elevar a qualidade da experiência de realizar o transporte de veículos.",
      image: {
        src: "satisfacao.svg",
        alt: "Satisfação de Nossos Clientes",
      },
    },
    {
      heading: "Inovação",
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat.",
      image: {
        src: "inovacao.svg",
        alt: "Inovação",
      },
    },
    {
      heading: "Transparência",
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat.",
      image: {
        src: "transparencia.svg",
        alt: "Transparência",
      },
    },
    {
      heading: "Tempo Real",
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat.",
      image: {
        src: "real-time.svg",
        alt: "Tempo real",
      },
    },
  ],
};
